import _typeof from "@babel/runtime/helpers/typeof";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _classCallCheck from "@babel/runtime/helpers/classCallCheck";
import _createClass from "@babel/runtime/helpers/createClass";
import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
var _excluded = ["retryConfig", "withToken", "withRequestId", "getRequestId", "getToken", "circuitBreaker", "logger"];
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == _typeof(i) ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != _typeof(t) || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != _typeof(i)) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import _regeneratorRuntime from "@babel/runtime/regenerator";
import axios from 'axios';
import axiosRetry, { exponentialDelay, isNetworkError, isSafeRequestError, isRetryableError } from 'axios-retry';
import compact from 'lodash.compact';
import { v4 as uuid } from 'uuid';
import Breaker from './breaker';
import { CANCEL_KEYS } from './consts';
// A global cancel token
var globalCancelController = new AbortController();
var tokenMap = new Map();

/**
 * The default retry config for axios-retry
 */
var DEFAULT_RETRY_CONFIG = {
  retries: 3,
  retryDelay: exponentialDelay,
  shouldResetTimeout: true,
  // Common retry config to only support HTTP method: 'get', 'head', 'options'
  retryCondition: function retryCondition(error) {
    return isNetworkError(error) || isSafeRequestError(error);
  }
};

/**
 * A retry config that won't check HTTP method. Could be applied to POST
 *
 * @param error - the error to check
 * @returns The retry condition
 */
var retryCondition = function retryCondition(error) {
  return isNetworkError(error) || isRetryableError(error);
};

/**
 * The axios interceptor to add request id to the request header.
 *
 * @param getRequestId - the function to get request id
 * @returns The axios interceptor
 */
var requestIdInterceptor = function requestIdInterceptor(getRequestId) {
  return function (config) {
    config.headers['x-request-id'] = typeof getRequestId === 'function' ? getRequestId(config) : uuid();
    return config;
  };
};

/**
 * The axios interceptor to add token to the request header.
 *
 * @param getToken - the function to get token
 * @returns The axios interceptor
 */
var tokenInterceptor = function tokenInterceptor(getToken) {
  return /*#__PURE__*/function () {
    var _ref = _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee(config) {
      var token;
      return _regeneratorRuntime.wrap(function _callee$(_context) {
        while (1) switch (_context.prev = _context.next) {
          case 0:
            if (!(typeof getToken !== 'function')) {
              _context.next = 2;
              break;
            }
            return _context.abrupt("return", config);
          case 2:
            _context.next = 4;
            return getToken();
          case 4:
            token = _context.sent;
            if (token !== null && token !== void 0 && token.accessToken) config.headers['Authorization'] = "Bearer ".concat(token.accessToken);
            return _context.abrupt("return", config);
          case 7:
          case "end":
            return _context.stop();
        }
      }, _callee);
    }));
    return function (_x) {
      return _ref.apply(this, arguments);
    };
  }();
};

/**
 * Cancel request by token id and remove the token from the map.
 *
 * @param tokenId - the token id to cancel
 */
var cancelByTokenId = function cancelByTokenId(tokenId) {
  if (tokenId && tokenMap.has(tokenId)) {
    tokenMap.get(tokenId)();
    tokenMap["delete"](tokenId);
  }
};

/**
 * Get a cancel signal with token id to cancel the request.
 * And also set the token id to the setter.
 *
 * @param setter - The setter to set the token id for future cancelation.
 * @returns The cancel signal
 */
var getCancelSignal = function getCancelSignal(setter) {
  var controller = new AbortController();
  var uniqId = uuid();
  tokenMap.set(uniqId, function () {
    return controller.abort();
  });
  setter(uniqId);
  return controller.signal;
};

/**
 * To cancel all pending requests, including
 *  1. pending request using global cancel token.
 *  2. pending request using specific cancel token.
 */
export var cancelAll = function cancelAll() {
  // Cancel all requests using global token
  globalCancelController.abort();
  globalCancelController = new AbortController();

  // Cancel all individual requests using specific token
  _toConsumableArray(tokenMap.values()).forEach(function (canceller) {
    return canceller();
  });
  tokenMap.clear();
};

/**
 * To batch cancel requests with token id. (Requests using global cancel token are not included.)
 *
 * @param tokens - the token ids to cancel
 */
export var cancelRequests = function cancelRequests() {
  var tokens = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
  compact(tokens).forEach(function (tokenId) {
    return cancelByTokenId(tokenId);
  });
};

/**
 * A basic http service that wrap Axios to handle http requests.
 */
var Service = /*#__PURE__*/function () {
  /** Instance */

  /**
   * @param config - the configurable options to initialize the http
   */
  function Service(config) {
    _classCallCheck(this, Service);
    this._initialize(config);
  }

  /**
   * Private method to initial axios
   */
  return _createClass(Service, [{
    key: "_initialize",
    value: function _initialize() {
      var config = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
      var retryConfig = config.retryConfig,
        withToken = config.withToken,
        withRequestId = config.withRequestId,
        getRequestId = config.getRequestId,
        getToken = config.getToken,
        circuitBreaker = config.circuitBreaker,
        logger = config.logger,
        others = _objectWithoutProperties(config, _excluded);

      // Create instance with retry
      var client = axios.create(others);
      axiosRetry(client, Object.assign({}, DEFAULT_RETRY_CONFIG, retryConfig));

      // decorator
      if (withToken !== false) {
        client.interceptors.request.use(tokenInterceptor(getToken));
      }
      if (withRequestId !== false) {
        client.interceptors.request.use(requestIdInterceptor(getRequestId));
      }

      // Add circuit breaker
      var breaker = new Breaker(circuitBreaker, logger);
      var reqInterceptor = breaker.requestInterceptor();
      var resInterceptor = breaker.responseInterceptor();
      client.interceptors.request.use(reqInterceptor.fulfilled, reqInterceptor.rejected);
      client.interceptors.response.use(resInterceptor.fulfilled, resInterceptor.rejected);
      this.request = client;
    }

    /**
     * Send request with cancel logic
     *
     * @param config - the configuration
     */
  }, {
    key: "sendRequest",
    value: function sendRequest() {
      var _globalCancelControll;
      var config = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
      var cancelKeys = config[CANCEL_KEYS],
        others = _objectWithoutProperties(config, [CANCEL_KEYS].map(_toPropertyKey));
      var _ref2 = cancelKeys || {},
        getter = _ref2.getter,
        setter = _ref2.setter,
        clearer = _ref2.clearer;

      // Cancel previous request if token `getter` provided
      if (typeof getter === 'function') {
        var token = getter();
        if (token) cancelByTokenId(token);
      }
      // Clear existing token if token `clearer` provided
      typeof clearer === 'function' && clearer();

      // Setup an individual cancel token only if `setter` provided.
      var tokenConfig = typeof setter === 'function' ? {
        signal: getCancelSignal(setter)
      } : {
        signal: (_globalCancelControll = globalCancelController) === null || _globalCancelControll === void 0 ? void 0 : _globalCancelControll.signal
      };
      var conf = Object.assign({}, others, tokenConfig);
      return this.request.request(conf);
    }

    /**
     * Http get wrapper
     *
     * @param url - the request url
     * @param config - the config to setup request headers or cancel tokens
     */
  }, {
    key: "get",
    value: function get(url, config) {
      return this.sendRequest(Object.assign({
        method: 'get',
        url: url
      }, config));
    }

    /**
     * Http post wrapper
     *
     * @param url - the request url
     * @param data - the request payload
     * @param config - the config to setup request headers or cancel tokens
     */
  }, {
    key: "post",
    value: function post(url, data, config) {
      return this.sendRequest(Object.assign({
        method: 'post',
        url: url,
        data: data
      }, config));
    }

    /**
     * Http post wrapper with retry logic since some API is using post to query data.
     *
     * @param url - the request url
     * @param data - the request payload
     * @param config - the config to setup request headers or cancel tokens
     */
  }, {
    key: "postWithRetry",
    value: function postWithRetry(url, data, config) {
      var conf = Object.assign({}, config, {
        'axios-retry': Object.assign({
          retryCondition: retryCondition
        }, config && config['axios-retry'])
      });
      return this.sendRequest(Object.assign({
        method: 'post',
        url: url,
        data: data
      }, conf));
    }

    /**
     * Http put wrapper
     *
     * @param url - the request url
     * @param data - the request payload
     * @param config - the config to setup request headers or cancel tokens
     */
  }, {
    key: "put",
    value: function put(url, data, config) {
      return this.sendRequest(Object.assign({
        method: 'put',
        url: url,
        data: data
      }, config));
    }

    /**
     * Http patch wrapper
     *
     * @param url - the request url
     * @param data - the request payload
     * @param config - the config to setup request headers or cancel tokens
     */
  }, {
    key: "patch",
    value: function patch(url, data, config) {
      return this.sendRequest(Object.assign({
        method: 'patch',
        url: url,
        data: data
      }, config));
    }

    /**
     * Http delete wrapper
     *
     * @param url - the request url
     * @param config - the config to setup request headers or cancel tokens
     */
  }, {
    key: "delete",
    value: function _delete(url, config) {
      return this.sendRequest(Object.assign({
        method: 'delete',
        url: url
      }, config));
    }
  }]);
}();
export { Service as default };